import React from 'react';

import * as TabPrimitives from '@radix-ui/react-tabs';

import cn from '../../utils/cn';
import Button from '../Button';

// Add the import statement for Button and ButtonProps

interface TabsProps extends React.ComponentProps<typeof TabPrimitives.Root> {
  children: React.ReactNode;
}

export type TabsButtonProps = React.ComponentProps<typeof Button> & {
  children: React.ReactNode;
};

const TabsButton = React.forwardRef((props: TabsButtonProps, ref) => {
  const { children, ...rest } = props;
  return (
    <Button
      {...rest}
      ref={ref as any}
      variant='ghost'
      data-test='tabs-button'
      className={cn(
        `flex h-14 flex-1 select-none items-center justify-center border-b-4 border-gray-light px-7
        py-4 text-gray-dark transition-all duration-200 ease-in-out
        hover:font-medium hover:text-gray-black
        data-[state=active]:border-primary
        data-[state=active]:font-bold
        data-[state=active]:text-primary
        data-[state=active]:focus:relative
        `
      )}
    >
      {children}
    </Button>
  );
});

TabsButton.displayName = 'TabsButton';

const TabsContent = ({ children, ...props }: React.ComponentProps<typeof TabPrimitives.Content>) => {
  return <TabPrimitives.Content {...props}>{children}</TabPrimitives.Content>;
};

TabsContent.displayName = 'TabsContent';

const TabsTrigger = ({ children, ...props }: React.ComponentProps<typeof TabPrimitives.Trigger>) => {
  return (
    <TabPrimitives.Trigger
      className={cn(
        `flex flex-1  select-none items-center  justify-center  border-b-4  border-gray-light px-7
        py-4 text-sm font-medium text-gray-black transition-all duration-200 ease-in-out
        hover:text-primary
        data-[state=active]:border-primary
        data-[state=active]:font-bold
        data-[state=active]:text-primary
        data-[state=active]:focus:relative
        data-[state=active]:focus:shadow-black
        `
      )}
      {...props}
    >
      {children}
    </TabPrimitives.Trigger>
  );
};

TabsTrigger.displayName = 'TabsTrigger';

const TabList = ({ children, className, ...props }: React.ComponentProps<typeof TabPrimitives.List>) => {
  return (
    <TabPrimitives.List className={cn('flex w-[300px] shrink-0 rounded-b-md border-gray-light', className)} {...props}>
      {children}
    </TabPrimitives.List>
  );
};

TabList.displayName = 'TabList';

const Tabs = ({ children, className, ...props }: TabsProps) => {
  return (
    <TabPrimitives.Root className={cn('flex w-full flex-col', className)} {...props}>
      {children}
    </TabPrimitives.Root>
  );
};

Tabs.displayName = 'Tabs';

Tabs.List = TabList;
Tabs.Trigger = TabsTrigger;
Tabs.Content = TabsContent;
Tabs.Button = TabsButton;

export default Tabs;
